import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  LinearProgress,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

/**
 * GettingStartedChecklist Component
 *
 * Onboarding checklist for new users
 *
 * Checklist Items:
 * - Add yourself to the family tree
 * - Add your parents
 * - Add your siblings
 * - Upload a family photo
 * - Invite a family member
 *
 * Features:
 * - Progress tracking
 * - Persistent state (saved to Firestore)
 * - Collapsible card
 * - Quick actions to complete each task
 *
 * @component
 * @returns {JSX.Element} Rendered checklist card
 */
export const GettingStartedChecklist: React.FC = () => {
  // ===== State =====
  const [expanded, setExpanded] = useState(true);
  const [checklist, setChecklist] = useState({
    addedSelf: false,
    addedParents: false,
    addedSiblings: false,
    uploadedPhoto: false,
    invitedMember: false,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { authState } = useAuth();

  // ===== Effects =====
  useEffect(() => {
    loadChecklist();
  }, [authState.user]);

  // ===== Helper Functions =====
  const loadChecklist = async () => {
    if (!authState.user) return;

    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', authState.user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.onboardingChecklist) {
          setChecklist(userData.onboardingChecklist);
        }
      }
    } catch (error) {
      console.error('Failed to load checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveChecklist = async (newChecklist: typeof checklist) => {
    if (!authState.user) return;

    try {
      await updateDoc(doc(db, 'users', authState.user.uid), {
        onboardingChecklist: newChecklist,
      });
    } catch (error) {
      console.error('Failed to save checklist:', error);
    }
  };

  const calculateProgress = (): number => {
    const items = Object.values(checklist);
    const completed = items.filter(Boolean).length;
    return Math.round((completed / items.length) * 100);
  };

  const isComplete = (): boolean => {
    return Object.values(checklist).every(Boolean);
  };

  // ===== Event Handlers =====
  const handleToggle = (key: keyof typeof checklist) => {
    const newChecklist = {
      ...checklist,
      [key]: !checklist[key],
    };
    setChecklist(newChecklist);
    saveChecklist(newChecklist);
  };

  const handleAction = (action: string) => {
    switch (action) {
      case 'addSelf':
      case 'addParents':
      case 'addSiblings':
        navigate('/people/add');
        break;
      case 'uploadPhoto':
        navigate('/people');
        break;
      case 'inviteMember':
        // Could open an invite dialog or navigate to settings
        navigate('/admin');
        break;
    }
  };

  // Don't show if complete
  if (isComplete() && !expanded) {
    return null;
  }

  const progress = calculateProgress();

  // ===== Render =====
  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              Getting Started
            </Typography>
            {isComplete() && <CheckCircleIcon color="success" />}
          </Box>
        }
        subheader={`${progress}% Complete`}
        action={
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
      />
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 2, height: 8, borderRadius: 4 }}
          />

          {isComplete() ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Congratulations!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You've completed the getting started checklist. You're all set to explore your family history!
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={checklist.addedSelf}
                    onChange={() => handleToggle('addedSelf')}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Add yourself to the family tree"
                  secondary={
                    !checklist.addedSelf && (
                      <Button
                        size="small"
                        onClick={() => handleAction('addSelf')}
                        sx={{ mt: 0.5 }}
                      >
                        Add Now
                      </Button>
                    )
                  }
                />
              </ListItem>

              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={checklist.addedParents}
                    onChange={() => handleToggle('addedParents')}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Add your parents"
                  secondary={
                    !checklist.addedParents && (
                      <Button
                        size="small"
                        onClick={() => handleAction('addParents')}
                        sx={{ mt: 0.5 }}
                      >
                        Add Now
                      </Button>
                    )
                  }
                />
              </ListItem>

              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={checklist.addedSiblings}
                    onChange={() => handleToggle('addedSiblings')}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Add your siblings"
                  secondary={
                    !checklist.addedSiblings && (
                      <Button
                        size="small"
                        onClick={() => handleAction('addSiblings')}
                        sx={{ mt: 0.5 }}
                      >
                        Add Now
                      </Button>
                    )
                  }
                />
              </ListItem>

              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={checklist.uploadedPhoto}
                    onChange={() => handleToggle('uploadedPhoto')}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Upload a family photo"
                  secondary={
                    !checklist.uploadedPhoto && (
                      <Button
                        size="small"
                        onClick={() => handleAction('uploadPhoto')}
                        sx={{ mt: 0.5 }}
                      >
                        Upload Now
                      </Button>
                    )
                  }
                />
              </ListItem>

              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    edge="start"
                    checked={checklist.invitedMember}
                    onChange={() => handleToggle('invitedMember')}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Invite a family member"
                  secondary={
                    !checklist.invitedMember && (
                      <Button
                        size="small"
                        onClick={() => handleAction('inviteMember')}
                        sx={{ mt: 0.5 }}
                      >
                        Invite Now
                      </Button>
                    )
                  }
                />
              </ListItem>
            </List>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};
